'use client';

import { useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Youtube,
  LogOut,
  X,
  ShieldCheck,
  Wallet,
  HelpCircle,
} from 'lucide-react';
import { CreditBadge } from '@/components/billing/CreditBadge';
import { trackEvent } from '@/lib/analytics/track';
import { Z_INDEX } from '@/lib/constants/zIndex';
import { useAuth } from '@/lib/hooks';
import { useOnboardingStore } from '@/lib/stores/onboardingStore';
import { useSidebarStore } from '@/lib/stores/uiStore';
import { cn } from '@/lib/utils';

/**
 * 더보기 메뉴 아이템 인터페이스
 */
interface MoreMenuItem {
  href?: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  divider?: boolean;
  /** true면 user.isStaff인 사용자에게만 노출 */
  adminOnly?: boolean;
}

/**
 * MoreMenu Props
 */
interface MoreMenuProps {
  className?: string;
}

/**
 * 더보기 바텀시트 메뉴 컴포넌트
 *
 * 모바일 탭바의 "더보기" 버튼 클릭 시 표시됩니다.
 * 파일, API 문서, 로그아웃 등의 추가 메뉴를 제공합니다.
 */
export function MoreMenu({ className }: MoreMenuProps) {
  const pathname = usePathname();
  const { isMobileMoreOpen, setMobileMoreOpen } = useSidebarStore();
  const { user, logout } = useAuth();
  const isStaff = user?.isStaff ?? false;
  const startOnboarding = useOnboardingStore((s) => s.startOnboarding);

  // 운영 메뉴는 /admin 페이지로 통합. 일반/관리자 모두 동일하게 채널 관리 + 로그아웃,
  // 관리자에게만 /admin 진입 추가.
  const allItems: MoreMenuItem[] = [
    {
      href: '/admin',
      label: '관리자',
      icon: <ShieldCheck className="w-5 h-5" />,
      adminOnly: true,
    },
    {
      href: '/channels',
      label: '채널 관리',
      icon: <Youtube className="w-5 h-5" />,
    },
    {
      href: '/settings/billing',
      label: '크레딧 & 결제',
      icon: <Wallet className="w-5 h-5" />,
    },
    {
      label: '투어 다시 보기',
      icon: <HelpCircle className="w-5 h-5" />,
      onClick: () => {
        trackEvent('onboarding_restart_clicked', { source: 'more_menu' });
        startOnboarding();
        setMobileMoreOpen(false);
      },
    },
    {
      label: '로그아웃',
      icon: <LogOut className="w-5 h-5" />,
      onClick: () => {
        logout();
        setMobileMoreOpen(false);
      },
      danger: true,
      divider: true,
    },
  ];

  const menuItems: MoreMenuItem[] = allItems.filter(
    (item) => !item.adminOnly || isStaff
  );

  // 메뉴 닫기 핸들러
  const closeMenu = useCallback(() => {
    setMobileMoreOpen(false);
  }, [setMobileMoreOpen]);

  // 경로 변경 시 메뉴 닫기
  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMoreOpen) {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMoreOpen, closeMenu]);

  // 스크롤 방지
  useEffect(() => {
    if (isMobileMoreOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMoreOpen]);

  if (!isMobileMoreOpen) {
    return null;
  }

  return (
    <>
      {/* 오버레이 배경 */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        style={{ zIndex: Z_INDEX.moreMenu - 1 }}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* 바텀시트 */}
      <div
        className={cn(
          'fixed left-0 right-0 bottom-0 bg-white rounded-t-2xl',
          'transform transition-transform duration-300 ease-out',
          'safe-area-inset-bottom',
          className
        )}
        style={{ zIndex: Z_INDEX.moreMenu }}
        role="dialog"
        aria-modal="true"
        aria-label="더보기 메뉴"
      >
        {/* 드래그 핸들 */}
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-neutral-300 rounded-full" />
        </div>

        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-neutral-100">
          <h2 className="text-lg font-semibold text-neutral-900">더보기</h2>
          <button
            onClick={closeMenu}
            className="p-2 rounded-full hover:bg-neutral-100 transition-colors"
            aria-label="닫기"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* 크레딧 잔액 카드 */}
        <div className="px-4 py-3 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-600">크레딧 잔액</span>
            <CreditBadge />
          </div>
        </div>

        {/* 메뉴 아이템 */}
        <nav className="py-2">
          {menuItems.map((item) => (
            <div key={item.label}>
              {item.divider && (
                <div className="my-2 border-t border-neutral-100" />
              )}

              {item.href ? (
                <Link
                  href={item.href}
                  onClick={closeMenu}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3',
                    'text-neutral-700 hover:bg-neutral-50 transition-colors',
                    item.danger && 'text-red-600 hover:bg-red-50'
                  )}
                >
                  <span className={item.danger ? 'text-red-500' : 'text-neutral-500'}>
                    {item.icon}
                  </span>
                  <span className="text-base font-medium">{item.label}</span>
                </Link>
              ) : (
                <button
                  onClick={item.onClick}
                  className={cn(
                    'flex items-center gap-3 w-full px-4 py-3',
                    'text-neutral-700 hover:bg-neutral-50 transition-colors',
                    item.danger && 'text-red-600 hover:bg-red-50'
                  )}
                >
                  <span className={item.danger ? 'text-red-500' : 'text-neutral-500'}>
                    {item.icon}
                  </span>
                  <span className="text-base font-medium">{item.label}</span>
                </button>
              )}
            </div>
          ))}
        </nav>

        {/* 하단 여백 (safe area) */}
        <div className="h-4" />
      </div>
    </>
  );
}

export default MoreMenu;
